import { Injectable, Optional } from '@angular/core';
import { catchError, firstValueFrom, map, throwError } from 'rxjs';

import { NavigationReadsService } from '../../services/navigation-reads.service';
import { ProductEnum } from '../authentication/models/product.enum';
import { AuthenticationService } from '../authentication/services/authentication.service';
import { IncreaseBalances } from '../shared/models/increase-pocket.interface';
import { BankService } from '../shared/services/bank.service';

@Injectable({ providedIn: 'root' })
export class AdvanceOnlyStateService {
  public userId?: number;
  public withoutAccount = false;
  private pending?: Promise<boolean>;
  private pendingUserId?: number;
  private pendingScope?: string;
  private sequence = 0;
  private latestResult?: Promise<boolean>;
  private revision = 0;

  constructor(
    private auth: AuthenticationService,
    private bank: BankService,
    @Optional() private navigationReads: NavigationReadsService | null = null,
  ) {}

  /** Apply a newer Home lookup without allowing an older pending lookup to overwrite it. */
  public applyBalances(userId: number, balances: IncreaseBalances): void {
    if (!userId || this.auth.getProfileState()?.id !== userId) return;
    this.revision++;
    this.userId = userId;
    this.withoutAccount = balances.pockets.length === 0;
  }

  public async refresh(): Promise<boolean> {
    const user = this.auth.getProfileState();
    if (this.userId !== user?.id) {
      this.userId = user?.id;
      this.withoutAccount = false;
    }
    if (
      !user?.id ||
      !user.productIds?.some((product) =>
        [ProductEnum.Advance, ProductEnum.Payment].includes(product),
      )
    ) {
      this.withoutAccount = false;
      return false;
    }
    const scope = this.navigationReads?.scopeKey('increase-state');
    if (
      this.pending &&
      this.pendingUserId === user.id &&
      this.pendingScope === scope
    )
      return this.pending;
    const userId = user.id;
    const request = ++this.sequence;
    const context = this.navigationReads?.contextVersion;
    const version = this.navigationReads?.version;
    const revision = this.revision;
    this.pendingUserId = userId;
    this.pendingScope = scope;
    const pending = firstValueFrom(
      this.bank.getIncreaseAccountState().pipe(
        catchError((error) =>
          error?.status === 404
            ? this.bank.getIncreasePocketBalances().pipe(
                map((balances) => ({
                  hasAccount: balances.pockets.length > 0,
                  hasCard: false,
                })),
              )
            : throwError(() => error),
        ),
      ),
    )
      .then(
        (state) => {
          const current = this.auth.getProfileState();
          if (current?.id !== userId) return false;
          if (revision !== this.revision) return this.withoutAccount;
          if (request !== this.sequence) return this.latestResult!;
          // The old-backend balance fallback is not itself a cached navigation read.
          // Its own key invalidation is fine; a newer navigation/write is not.
          if (
            context !== this.navigationReads?.contextVersion ||
            version !== this.navigationReads?.version
          )
            return this.refresh();
          if (
            !current.productIds?.some((product) =>
              [ProductEnum.Advance, ProductEnum.Payment].includes(product),
            )
          )
            return false;
          this.withoutAccount = !state.hasAccount;
          return this.withoutAccount;
        },
        (error) => {
          if (this.auth.getProfileState()?.id === userId) {
            // A live balance result is newer than this snapshot, including its failure.
            if (revision !== this.revision) return this.withoutAccount;
            if (request !== this.sequence) return this.latestResult!;
            if (scope !== this.navigationReads?.scopeKey('increase-state'))
              return this.refresh();
            this.withoutAccount = false;
          }
          throw error;
        },
      )
      .finally(() => {
        if (this.pending === pending) this.pending = undefined;
      });
    this.pending = pending;
    this.latestResult = pending;
    return pending;
  }
}
